import { AnyAction, Dispatch } from "redux";
import { websiteBuilderAddNode, websiteBuilderSelectNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { isWebsiteBuilderStretchElementData, TWebsiteBuilderElement } from "../../../nodes/element/websiteBuilderElement";
import { cloneDeep } from "lodash";
import { TWebsiteBuilderSection } from "../../../nodes/section/websiteBuilderSection";
import { TWebsiteBuilderDevice } from "../../../website/shared/device";
import { IWebsiteBuilderFlex } from "../../../nodes/container/websiteBuilderFlex";
import { IWebsiteBuilderGrid, isWebsiteBuilderGrid } from "../../../nodes/container/websiteBuilderGrid";
import { IWebsiteBuilderGridCell, websiteBuilderGridCellFilled } from "../../../nodes/gridCell/websiteBuilderGridCell";
import { IWebsiteBuilderTabset, isWebsiteBuilderTabset } from "../../../nodes/container/websiteBuilderTabset";
import { IWebsiteBuilderTab } from "../../../nodes/tab/websiteBuilderTab";

function createElementFlowParentNodeInsertNode(node: TWebsiteBuilderElement): TWebsiteBuilderElement {
  const nodeToInsert: TWebsiteBuilderElement = cloneDeep(node);

  nodeToInsert.element.layout.desktop.position = { x: 0, y: 0 };
  nodeToInsert.element.layout.tablet.position = { x: 0, y: 0 };
  nodeToInsert.element.layout.mobile.position = { x: 0, y: 0 };

  nodeToInsert.element.zIndex = 0;

  return nodeToInsert;
}

export function websiteBuilderInsertElementIntoFlexContainer(
  node: TWebsiteBuilderElement,
  targetNode: IWebsiteBuilderFlex,
  sectionId: string,
  pageId: number,
  device: TWebsiteBuilderDevice,
  dispatch: Dispatch<AnyAction>,
) {
  if (isWebsiteBuilderStretchElementData(node.element) && node.element.stretch.enabled) {
    return;
  }
  
  const nodeToInsert = createElementFlowParentNodeInsertNode(node);
  
  const addNodeAction = websiteBuilderAddNode(pageId, device, nodeToInsert.id, {
    snapshot: {
      inverseScrollNodeId: targetNode.id,
      parentId: targetNode.id,
      node: nodeToInsert,
      index: targetNode.children.length,
    },
  });

  if (addNodeAction == null) {
    return;
  }

  dispatch(addNodeAction);

  dispatch(
    websiteBuilderSelectNode({
      selectedNode: {
        type: "element",
        sectionId,
        containerId: targetNode.id,
        gridCellId: null,
        tabId: null,
        id: nodeToInsert.id,
        editingMode: {
          type: "none",
        },
      },
    }),
  );
}

export function websiteBuilderInsertElementIntoGridContainer(
  node: TWebsiteBuilderElement,
  targetNode: IWebsiteBuilderGrid,
  sectionId: string,
  pageId: number,
  device: TWebsiteBuilderDevice,
  dispatch: Dispatch<AnyAction>,
) {
  if (isWebsiteBuilderStretchElementData(node.element) && node.element.stretch.enabled) {
    return;
  }
  
  const unfilledGridCell = targetNode.children.find(child => !websiteBuilderGridCellFilled(child));

  if (unfilledGridCell == null) {
    return;
  }

  const nodeToInsert = createElementFlowParentNodeInsertNode(node);

  const addNodeAction = websiteBuilderAddNode(pageId, device, nodeToInsert.id, {
    snapshot: {
      inverseScrollNodeId: unfilledGridCell.id,
      parentId: unfilledGridCell.id,
      node: nodeToInsert,
      index: 0,
    },
  });

  if (addNodeAction == null) {
    return;
  }

  dispatch(addNodeAction);

  dispatch(
    websiteBuilderSelectNode({
      selectedNode: {
        type: "element",
        sectionId,
        containerId: targetNode.id,
        gridCellId: unfilledGridCell.id,
        tabId: null,
        id: nodeToInsert.id,
        editingMode: {
          type: "none",
        },
      },
    }),
  );
}

interface IWebsiteBuilderGridCellAncestry {
  gridNode: IWebsiteBuilderGrid;
  gridCellIndex: number;
}

export function websiteBuilderGetGridCellAncestry(
  sectionNode: TWebsiteBuilderSection,
  gridCellNode: IWebsiteBuilderGridCell,
): IWebsiteBuilderGridCellAncestry {
  for (const child of sectionNode.children) {
    if (!isWebsiteBuilderGrid(child)) {
      continue;
    }

    const gridCellIndex = child.children.findIndex(cell => cell.id === gridCellNode.id);

    if (gridCellIndex !== -1) {
      return {
        gridNode: child,
        gridCellIndex,
      };
    }
  }

  return null;
}

interface IWebsiteBuilderTabAncestry {
  tabsetNode: IWebsiteBuilderTabset;
}

export function websiteBuilderGetTabAncestry(
  sectionNode: TWebsiteBuilderSection,
  tabNode: IWebsiteBuilderTab,
): IWebsiteBuilderTabAncestry {
  for (const child of sectionNode.children) {
    if (!isWebsiteBuilderTabset(child)) {
      continue;
    }

    if (child.children.some(tab => tab.id === tabNode.id)) {
      return {
        tabsetNode: child,
      };
    }
  }

  return null;
}

export function websiteBuilderInsertElementIntoNextGridCell(
  node: TWebsiteBuilderElement,
  targetNode: IWebsiteBuilderGridCell,
  targetSectionNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
  dispatch: Dispatch<AnyAction>,
) {
  if (isWebsiteBuilderStretchElementData(node.element) && node.element.stretch.enabled) {
    return;
  }

  const gridCellAncestry = websiteBuilderGetGridCellAncestry(targetSectionNode, targetNode);

  if (gridCellAncestry == null) {
    return;
  }

  const nextUnfilledGridCellIndex = gridCellAncestry.gridNode.children.findIndex((cell, index) => {
    return index > gridCellAncestry.gridCellIndex && !websiteBuilderGridCellFilled(cell);
  });

  if (nextUnfilledGridCellIndex === -1) {
    return;
  }

  const nextGridCell = gridCellAncestry.gridNode.children[nextUnfilledGridCellIndex];

  if (nextGridCell == null) {
    return;
  }

  const nodeToInsert = createElementFlowParentNodeInsertNode(node);

  const addNodeAction = websiteBuilderAddNode(pageId, device, nodeToInsert.id, {
    snapshot: {
      inverseScrollNodeId: nextGridCell.id,
      parentId: nextGridCell.id,
      node: nodeToInsert,
      index: 0,
    },
  });

  if (addNodeAction == null) {
    return;
  }

  dispatch(addNodeAction);

  dispatch(
    websiteBuilderSelectNode({
      selectedNode: {
        type: "element",
        sectionId: targetSectionNode.id,
        containerId: gridCellAncestry.gridNode.id,
        gridCellId: nextGridCell.id,
        tabId: null,
        id: nodeToInsert.id,
        editingMode: {
          type: "none",
        },
      },
    }),
  );
}

export function websiteBuilderInsertElementIntoGridCell(
  node: TWebsiteBuilderElement,
  targetNode: IWebsiteBuilderGridCell,
  targetGridNode: IWebsiteBuilderGrid,
  targetSectionNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
  dispatch: Dispatch<AnyAction>,
) {
  if (
    (isWebsiteBuilderStretchElementData(node.element) && node.element.stretch.enabled) ||
    websiteBuilderGridCellFilled(targetNode)
  ) {
    return;
  }

  const nodeToInsert = createElementFlowParentNodeInsertNode(node);

  const addNodeAction = websiteBuilderAddNode(pageId, device, nodeToInsert.id, {
    snapshot: {
      inverseScrollNodeId: targetNode.id,
      parentId: targetNode.id,
      node: nodeToInsert,
      index: 0,
    },
  });

  if (addNodeAction == null) {
    return;
  }

  dispatch(addNodeAction);

  dispatch(
    websiteBuilderSelectNode({
      selectedNode: {
        type: "element",
        sectionId: targetSectionNode.id,
        containerId: targetGridNode.id,
        gridCellId: targetNode.id,
        tabId: null,
        id: nodeToInsert.id,
        editingMode: {
          type: "none",
        },
      },
    }),
  );
}
